import * as THREE from "../../libs/three.js/build/three.module.js";
import { PointCloudOctreeGeometry, PointCloudOctreeGeometryNode } from "../PointCloudOctreeGeometry.js";
import { PointAttribute, PointAttributes, PointAttributeTypes } from "./PointAttributes.js";
import { XHRFactory } from "../XHRFactory.js";
import {boolean} from "mathjs";
import lz4 from 'lz4js';

/**
 * PCD 点云数据加载器
 * 支持 ASCII 和 Binary 格式的 PCD 文件
 */
export class PCDLoader {
    /**
     * 温度到 RGB 的颜色映射表（jet colormap）
     * 从低到高温度对应：蓝 → 青 → 绿 → 黄 → 红
     */
    static COLOR_MAP = {
        r: [0.050383, 0.063536, 0.075353, 0.086222, 0.096379, 0.105980, 0.115124, 0.123903, 0.132381, 0.140603, 0.148607, 0.156421, 0.164070, 0.171574, 0.178950, 0.186213, 0.193374, 0.200445, 0.207435, 0.214350, 0.221197, 0.227983, 0.234715, 0.241396, 0.248032, 0.254627, 0.261183, 0.267703, 0.274191, 0.280648, 0.287076, 0.293478, 0.299855, 0.306210, 0.312543, 0.318856, 0.325150, 0.331426, 0.337683, 0.343925, 0.350150, 0.356359, 0.362553, 0.368733, 0.374897, 0.381047, 0.387183, 0.393304, 0.399411, 0.405503, 0.411580, 0.417642, 0.423689, 0.429719, 0.435734, 0.441732, 0.447714, 0.453677, 0.459623, 0.465550, 0.471457, 0.477344, 0.483210, 0.489055, 0.494877, 0.500678, 0.506454, 0.512206, 0.517933, 0.523633, 0.529306, 0.534952, 0.540570, 0.546157, 0.551715, 0.557243, 0.562738, 0.568201, 0.573632, 0.579029, 0.584391, 0.589719, 0.595011, 0.600266, 0.605485, 0.610667, 0.615812, 0.620919, 0.625987, 0.631017, 0.636008, 0.640959, 0.645872, 0.650746, 0.655580, 0.660374, 0.665129, 0.669845, 0.674522, 0.679160, 0.683758, 0.688318, 0.692840, 0.697324, 0.701769, 0.706178, 0.710549, 0.714883, 0.719181, 0.723444, 0.727670, 0.731862, 0.736019, 0.740143, 0.744232, 0.748289, 0.752312, 0.756304, 0.760264, 0.764193, 0.768090, 0.771958, 0.775796, 0.779604, 0.783383, 0.787133, 0.790855, 0.794549, 0.798216, 0.801855, 0.805467, 0.809052, 0.812612, 0.816144, 0.819651, 0.823132, 0.826588, 0.830018, 0.833422, 0.836801, 0.840155, 0.843484, 0.846788, 0.850066, 0.853319, 0.856547, 0.859750, 0.862927, 0.866078, 0.869203, 0.872303, 0.875376, 0.878423, 0.881443, 0.884436, 0.887402, 0.890340, 0.893250, 0.896131, 0.898984, 0.901807, 0.904601, 0.907365, 0.910098, 0.912800, 0.915471, 0.918109, 0.920714, 0.923287, 0.925825, 0.928329, 0.930798, 0.933232, 0.935630, 0.937990, 0.940313, 0.942598, 0.944844, 0.947051, 0.949217, 0.951344, 0.953428, 0.955470, 0.957469, 0.959424, 0.961336, 0.963203, 0.965024, 0.966798, 0.968526, 0.970205, 0.971835, 0.973416, 0.974947, 0.976428, 0.977856, 0.979233, 0.980556, 0.981826, 0.983041, 0.984199, 0.985301, 0.986345, 0.987332, 0.988260, 0.989128, 0.989935, 0.990681, 0.991365, 0.991985, 0.992541, 0.993032, 0.993456, 0.993814, 0.994103, 0.994324, 0.994474, 0.994553, 0.994561, 0.994495, 0.994355, 0.994141, 0.993851, 0.993482, 0.993033, 0.992505, 0.991897, 0.991209, 0.990439, 0.989587, 0.988648, 0.987621, 0.986509, 0.985314, 0.984031, 0.982653, 0.981190, 0.979644, 0.977995, 0.976265, 0.974443, 0.972530, 0.970533, 0.968443, 0.966271, 0.964021, 0.961681, 0.959276, 0.956808, 0.954287, 0.951726, 0.949151, 0.946602, 0.944152, 0.941896, 0.940015],
        g: [0.029803, 0.028426, 0.027206, 0.026125, 0.025165, 0.024309, 0.023556, 0.022878, 0.022258, 0.021687, 0.021154, 0.020651, 0.020171, 0.019706, 0.019252, 0.018803, 0.018354, 0.017902, 0.017442, 0.016973, 0.016497, 0.016007, 0.015502, 0.014979, 0.014439, 0.013882, 0.013308, 0.012716, 0.012109, 0.011488, 0.010855, 0.010213, 0.009561, 0.008902, 0.008239, 0.007576, 0.006915, 0.006261, 0.005618, 0.004991, 0.004382, 0.003798, 0.003243, 0.002724, 0.002245, 0.001814, 0.001434, 0.001114, 0.000859, 0.000678, 0.000577, 0.000564, 0.000646, 0.000831, 0.001127, 0.001540, 0.002080, 0.002755, 0.003574, 0.004545, 0.005678, 0.006980, 0.008460, 0.010127, 0.011990, 0.014055, 0.016333, 0.018833, 0.021563, 0.024532, 0.027747, 0.031217, 0.034950, 0.038954, 0.043136, 0.047331, 0.051545, 0.055778, 0.060028, 0.064296, 0.068579, 0.072878, 0.077190, 0.081516, 0.085854, 0.090204, 0.094564, 0.098934, 0.103312, 0.107699, 0.112092, 0.116492, 0.120898, 0.125309, 0.129725, 0.134144, 0.138566, 0.142992, 0.147419, 0.151848, 0.156278, 0.160709, 0.165141, 0.169573, 0.174005, 0.178437, 0.182868, 0.187299, 0.191729, 0.196158, 0.200586, 0.205013, 0.209439, 0.213864, 0.218288, 0.222711, 0.227133, 0.231555, 0.235976, 0.240396, 0.244817, 0.249237, 0.253658, 0.258078, 0.262500, 0.266922, 0.271345, 0.275770, 0.280197, 0.284626, 0.289057, 0.293491, 0.297928, 0.302368, 0.306812, 0.311261, 0.315714, 0.320172, 0.324635, 0.329105, 0.333580, 0.338062, 0.342551, 0.347048, 0.351553, 0.356066, 0.360588, 0.365119, 0.369660, 0.374212, 0.378774, 0.383347, 0.387932, 0.392529, 0.397139, 0.401762, 0.406398, 0.411048, 0.415712, 0.420392, 0.425087, 0.429797, 0.434524, 0.439268, 0.444029, 0.448807, 0.453603, 0.458417, 0.463251, 0.468103, 0.472975, 0.477867, 0.482780, 0.487712, 0.492667, 0.497642, 0.502639, 0.507658, 0.512699, 0.517763, 0.522850, 0.527960, 0.533093, 0.538250, 0.543431, 0.548636, 0.553865, 0.559118, 0.564396, 0.569700, 0.575028, 0.580382, 0.585761, 0.591165, 0.596595, 0.602051, 0.607532, 0.613039, 0.618572, 0.624131, 0.629718, 0.635330, 0.640969, 0.646633, 0.652325, 0.658043, 0.663787, 0.669558, 0.675355, 0.681179, 0.687030, 0.692907, 0.698810, 0.704741, 0.710698, 0.716681, 0.722691, 0.728728, 0.734791, 0.740880, 0.746995, 0.753137, 0.759304, 0.765499, 0.771720, 0.777967, 0.784239, 0.790537, 0.796859, 0.803205, 0.809579, 0.815978, 0.822401, 0.828846, 0.835315, 0.841812, 0.848329, 0.854866, 0.861432, 0.868016, 0.874622, 0.881250, 0.887896, 0.894564, 0.901249, 0.907950, 0.914672, 0.921407, 0.928152, 0.934908, 0.941671, 0.948435, 0.955190, 0.961916, 0.968590, 0.975158],
        b: [0.527975, 0.533124, 0.538007, 0.542658, 0.547103, 0.551368, 0.555468, 0.559423, 0.563250, 0.566959, 0.570562, 0.574065, 0.577478, 0.580806, 0.584054, 0.587228, 0.590330, 0.593364, 0.596333, 0.599239, 0.602083, 0.604867, 0.607592, 0.610259, 0.612868, 0.615419, 0.617911, 0.620346, 0.622722, 0.625038, 0.627295, 0.629490, 0.631624, 0.633694, 0.635700, 0.637640, 0.639512, 0.641316, 0.643049, 0.644710, 0.646298, 0.647810, 0.649245, 0.650601, 0.651876, 0.653068, 0.654177, 0.655199, 0.656133, 0.656977, 0.657730, 0.658390, 0.658956, 0.659425, 0.659797, 0.660069, 0.660240, 0.660310, 0.660277, 0.660139, 0.659897, 0.659549, 0.659095, 0.658534, 0.657865, 0.657088, 0.656202, 0.655209, 0.654109, 0.652901, 0.651586, 0.650165, 0.648640, 0.647010, 0.645277, 0.643443, 0.641509, 0.639477, 0.637349, 0.635126, 0.632812, 0.630408, 0.627917, 0.625342, 0.622686, 0.619951, 0.617140, 0.614257, 0.611305, 0.608287, 0.605205, 0.602065, 0.598867, 0.595617, 0.592317, 0.588971, 0.585582, 0.582154, 0.578688, 0.575189, 0.571660, 0.568103, 0.564522, 0.560919, 0.557296, 0.553657, 0.550004, 0.546338, 0.542663, 0.538981, 0.535293, 0.531601, 0.527908, 0.524216, 0.520524, 0.516834, 0.513149, 0.509468, 0.505794, 0.502126, 0.498465, 0.494813, 0.491171, 0.487539, 0.483918, 0.480307, 0.476706, 0.473117, 0.469538, 0.465971, 0.462415, 0.458870, 0.455338, 0.451816, 0.448306, 0.444806, 0.441316, 0.437836, 0.434366, 0.430905, 0.427455, 0.424013, 0.420579, 0.417153, 0.413734, 0.410322, 0.406917, 0.403519, 0.400126, 0.396738, 0.393355, 0.389976, 0.386600, 0.383229, 0.379860, 0.376494, 0.373130, 0.369768, 0.366407, 0.363047, 0.359688, 0.356329, 0.352970, 0.349610, 0.346251, 0.342890, 0.339529, 0.336166, 0.332801, 0.329435, 0.326067, 0.322697, 0.319325, 0.315952, 0.312575, 0.309197, 0.305816, 0.302433, 0.299049, 0.295662, 0.292275, 0.288883, 0.285490, 0.282096, 0.278701, 0.275305, 0.271909, 0.268513, 0.265118, 0.261721, 0.258325, 0.254931, 0.251540, 0.248151, 0.244767, 0.241387, 0.238013, 0.234646, 0.231287, 0.227937, 0.224595, 0.221265, 0.217948, 0.214648, 0.211364, 0.208100, 0.204859, 0.201642, 0.198453, 0.195295, 0.192170, 0.189084, 0.186041, 0.183043, 0.180097, 0.177208, 0.174381, 0.171622, 0.168938, 0.166335, 0.163821, 0.161404, 0.159092, 0.156891, 0.154808, 0.152855, 0.151042, 0.149377, 0.147870, 0.146529, 0.145357, 0.144363, 0.143557, 0.142945, 0.142528, 0.142303, 0.142279, 0.142453, 0.142808, 0.143351, 0.144061, 0.144923, 0.145919, 0.147014, 0.148180, 0.149370, 0.150520, 0.151566, 0.152409, 0.152921, 0.152925, 0.152178, 0.150328, 0.146861, 0.140956, 0.131326]
    };

    /**
     * 根据温度值计算对应的 RGB 颜色（Jet Colormap）
     * @param {number} temp 温度值
     * @param {number} min 最小温度
     * @param {number} max 最大温度
     * @returns {Object} {r, g, b} 0-255 范围的 RGB 值
     */
    static colorMap(temp, min, max) {
        // 将温度映射到 0-255 范围
        const alpha = 255.0 / (max - min);
        const beta = -alpha * min;
        let p = alpha * temp + beta;

        // 限制在 0-255 范围内
        if (p < 0) p = 0;
        if (p > 255) p = 255;

        // 将 p 转换为整数索引
        const index = Math.floor(p);

        // 从颜色映射表中获取 RGB 值（归一化的 0-1 值）
        const r = this.COLOR_MAP.r[index];
        const g = this.COLOR_MAP.g[index];
        const b = this.COLOR_MAP.b[index];

        // 转换为 0-255 范围并返回
        return {
            r: Math.floor(r * 255.0),
            g: Math.floor(g * 255.0),
            b: Math.floor(b * 255.0)
        };
    }

    /**
     * 加载 PCD 文件
     * @param {string} url PCD 文件路径
     * @param {Function} callback 加载完成回调
     */
    static loadSinglePcd(url, callback) {
        // 首先加载 PCD 文件
        this.loadBinaryData(url, (pcdBuffer) => {
            if (!pcdBuffer) {
                callback(null);
                return;
            }

            // 尝试加载额外的二进制数据（如 colorize_data.bin）
            const colorizeUrl = url.substring(0, url.lastIndexOf('/') + 1) + 'colorize_data.bin';
            this.loadBinaryData(colorizeUrl, (colorizeBuffer) => {
                if (colorizeBuffer) {
                    console.log('额外二进制数据加载成功:', colorizeUrl);
                } else {
                    console.log('未找到额外二进制数据，仅使用 PCD 内部数据');
                }
                // 同时传递 PCD 数据和额外的二进制数据
                // temperatureBinBuffer 可在 parseSinglePcd 方法中使用
                this.parseSinglePcd(pcdBuffer, url, callback, colorizeBuffer);
            });
        });
    }

    /**
     * 加载二进制数据的通用方法
     * @param {string} url 文件路径
     * @param {Function} callback 加载完成回调，参数为 (ArrayBuffer)
     */
    static loadBinaryData(url, callback) {
        try {
            const xhr = XHRFactory.createXMLHttpRequest();
            xhr.open('GET', url, true);
            xhr.responseType = 'arraybuffer';
            xhr.onreadystatechange = () => {
                if (xhr.readyState === 4) {
                    if (xhr.status === 200 || xhr.status === 0) {
                        callback(xhr.response);
                    } else {
                        callback(null);
                    }
                }
            };
            xhr.send(null);
        } catch (e) {
            console.error(`加载二进制文件错误: ${url}, ${e}`);
            callback(null);
        }
    }

    /**
     * 解析 PCD 文件缓冲区
     * @param {ArrayBuffer} buffer 文件缓冲区
     * @param {string} url 文件路径
     * @param {Function} callback 解析完成回调
     * @param {ArrayBuffer} temperatureBinBuffer 额外的二进制数据缓冲区（可选）
     *
     * 使用示例：
     * if (temperatureBinBuffer) {
     *     // 使用 LZ4 解压额外数据
     *     const decompressedBuffer = this.decompressLZ4(temperatureBinBuffer);
     *     if (decompressedBuffer) {
     *         const dataView = new DataView(decompressedBuffer);
     *         // 自定义解析逻辑...
     *         // 将数据合并到 pointData 中
     *     }
     * }
     */
    static async parseSinglePcd(buffer, url, callback, temperatureBinBuffer = null) {
        try {
            // 将 buffer 转换为文本读取头部信息
            const decoder = new TextDecoder('utf-8');
            const headerText = decoder.decode(buffer.slice(0, Math.min(5000, buffer.byteLength)));
            const headerLines = headerText.split('\n');

            // 解析头部信息
            const header = this.parseHeader(headerLines);

            if (!header) {
                console.error('Invalid PCD header');
                callback(null);
                return;
            }

            console.log('PCD Header:', header);

            // 计算头部结束位置（找到 "DATA" 行）
            const dataStartIndex = headerText.indexOf('DATA binary') + 'DATA binary'.length + 1;
            const dataBuffer = buffer.slice(dataStartIndex);

            // 解析点云数据
            const pointData = this.parseBinaryData(dataBuffer, header);

            // ================== 解压和使用额外二进制数据 ==================
            if (temperatureBinBuffer) {
                try {
                    // 使用 LZ4 解压额外数据（decompressLZ4 内部已跳过前2个float）
                    const decompressedBuffer = this.decompressLZ4(temperatureBinBuffer);
                    if (decompressedBuffer) {
                        console.log('LZ4 解压成功，解压后数据大小:', decompressedBuffer.byteLength);

                        const numPoints = pointData.length;
                        // 确保字节长度是4的倍数（float对齐）
                        const alignedLen = Math.floor(decompressedBuffer.byteLength / 4) * 4;
                        const asFloat = new Float32Array(decompressedBuffer.slice(0, alignedLen));
                        console.log('Float32 数量: ' + asFloat.length + ', 点数: ' + numPoints);

                        for (let i = 0; i < numPoints; i++) {
                            if (i < asFloat.length) {
                                pointData[i].temperatureValue = asFloat[i];
                            } else {
                                pointData[i].temperatureValue = 10000.0;
                            }
                        }
                        console.log('额外数据已融合到点云中（Float32格式）');
                    } else {
                        console.warn('LZ4 解压失败，将直接使用原始数据 10000.0');

                        for (let i = 0; i < pointData.length; i++) {
                            pointData[i].temperatureValue = 10000.0;
                        }
                    }
                } catch (e) {
                    console.error('解压或处理额外数据时出错:', e);
                }
            }
            // =============================================================

            // 计算边界框
            const boundingBox = this.computeBoundingBox(pointData);
            const offset = boundingBox.min.clone();

            // 对boundingBox应用offset（仿照POCLoader）
            const tightBoundingBox = boundingBox.clone();
            boundingBox.min.sub(offset);
            boundingBox.max.sub(offset);
            tightBoundingBox.min.sub(offset);
            tightBoundingBox.max.sub(offset);

            // 计算点间距
            const spacing = this.computeSpacing(boundingBox, header.pointsCount);

            // 创建点云几何结构
            const pco = new PointCloudOctreeGeometry();
            pco.url = url;
            pco.octreeDir = url.substring(0, url.lastIndexOf('/'));
            pco.boundingBox = boundingBox;
            pco.tightBoundingBox = tightBoundingBox;
            pco.boundingSphere = boundingBox.getBoundingSphere(new THREE.Sphere());
            pco.offset = offset;
            pco.scale = new THREE.Vector3(1, 1, 1);
            pco.spacing = spacing;
            pco.pointAttributes = pcdAttributes(header);
            pco.loader = null; // PCD 不需要特殊加载器
            pco.version = null; // PCD 没有版本号

            // 创建根节点
            const root = new PointCloudOctreeGeometryNode(
                url.substring(url.lastIndexOf('/') + 1, url.length),
                pco,
                boundingBox
            );
            root.level = 0;
            root.numPoints = header.pointsCount;
            root.hasChildren = false; // PCD没有子节点
            root.spacing = spacing;

            // 创建 BufferGeometry
            // 如果需要使用额外数据，可以在这里处理 temperatureBinBuffer
            root.geometry = this.createBufferGeometry(pointData, header, offset);

            root.loaded = true;
            pco.root = root;
            pco.nodes = { 'r': root }; // 添加nodes映射

            console.log(`PCD loaded successfully: ${header.pointsCount} points`);
            callback(pco);
        } catch (e) {
            console.error(`Error parsing PCD file: ${e}`);
            callback(null);
        }
    }

    /**
     * 解析 PCD 头部
     * @param {Array<string>} lines 头部行
     * @returns {Object} 头部信息
     */
    static parseHeader(lines) {
        const header = {
            version: null,
            fields: [],
            sizes: [],
            types: [],
            counts: [],
            width: 0,
            height: 0,
            viewpoint: null,
            pointsCount: 0,
            dataFormat: null,
            pointSize: 0
        };

        for (const line of lines) {
            const trimmed = line.trim();
            if (trimmed.startsWith('#') || trimmed === '') continue;

            const parts = trimmed.split(/\s+/);

            switch (parts[0]) {
                case 'VERSION':
                    header.version = parts[1];
                    break;
                case 'FIELDS':
                    header.fields = parts.slice(1);
                    break;
                case 'SIZE':
                    header.sizes = parts.slice(1).map(s => parseInt(s));
                    break;
                case 'TYPE':
                    header.types = parts.slice(1);
                    break;
                case 'COUNT':
                    header.counts = parts.slice(1).map(c => parseInt(c));
                    break;
                case 'WIDTH':
                    header.width = parseInt(parts[1]);
                    break;
                case 'HEIGHT':
                    header.height = parseInt(parts[1]);
                    break;
                case 'VIEWPOINT':
                    header.viewpoint = parts.slice(1);
                    break;
                case 'POINTS':
                    header.pointsCount = parseInt(parts[1]);
                    break;
                case 'DATA':
                    header.dataFormat = parts[1];
                    break;
            }

            // 找到 DATA 行后停止解析
            if (parts[0] === 'DATA') break;
        }

        // 计算每个点的大小
        header.pointSize = 0;
        for (let i = 0; i < header.sizes.length; i++) {
            header.pointSize += header.sizes[i] * (header.counts[i] || 1);
        }

        // 验证头部
        if (!header.version || !header.fields.length || !header.pointsCount) {
            return null;
        }

        return header;
    }

    /**
     * 使用 LZ4 解压二进制数据
     * 数据格式：[4字节 int: 压缩前长度] [4字节 int: 未使用] [LZ4压缩数据]
     * @param {ArrayBuffer} compressedBuffer 压缩的数据缓冲区
     * @returns {ArrayBuffer|null} 解压后的数据缓冲区，失败返回 null
     */
    static decompressLZ4(compressedBuffer) {
        try {
            if (!compressedBuffer || compressedBuffer.byteLength === 0) {
                console.warn('空的压缩数据');
                return null;
            }

            console.log('开始 LZ4 解压，压缩数据总大小:', compressedBuffer.byteLength);

            // 读取头部的两个 C++ int（每个 4 字节）
            const dataView = new DataView(compressedBuffer);
            const originalDataLength = dataView.getInt32(0, true);   // 压缩前原始数据字节数
            const headerValue1 = dataView.getInt32(4, true);

            console.log('头部信息 - 原始数据长度:', originalDataLength, '[1]:', headerValue1);

            // 跳过头部 8 字节，提取实际的 LZ4 压缩数据
            const headerSize = 8;
            const compressedDataOnly = compressedBuffer.slice(headerSize);
            const compressedUint8 = new Uint8Array(compressedDataOnly);

            console.log('跳过头部后，实际 LZ4 数据大小:', compressedUint8.byteLength);

            // 检测是否为 LZ4 Frame 格式（magic number: 0x184D2204）
            const magicNum = (compressedUint8[0] | (compressedUint8[1] << 8) | (compressedUint8[2] << 16) | (compressedUint8[3] << 24)) >>> 0;
            const isFrameFormat = (magicNum === 0x184D2204);
            console.log(`检测到 magic: 0x${magicNum.toString(16)}, ${isFrameFormat ? 'Frame模式' : 'Block模式'}`);

            let decompressedUint8;

            if (isFrameFormat) {
                decompressedUint8 = lz4.decompress(compressedUint8);
                console.log('LZ4 Frame 解压成功，大小:', decompressedUint8.byteLength);
            } else {
                // Block 模式：用头部记录的原始长度作为缓冲区大小
                const bufferSize = Math.max(originalDataLength || 0, compressedUint8.byteLength * 10);
                const decompressedBuffer = lz4.makeBuffer(bufferSize);
                const decompressedSizeActual = lz4.decompressBlock(
                    compressedUint8,
                    decompressedBuffer,
                    0,
                    compressedUint8.length,
                    0
                );

                if (decompressedSizeActual > 0) {
                    decompressedUint8 = new Uint8Array(decompressedBuffer.buffer, 0, decompressedSizeActual);
                    console.log('LZ4 Block 解压成功，大小:', decompressedUint8.byteLength);
                } else {
                    console.error('LZ4 Block 解压失败');
                    return null;
                }
            }

            console.log('解压完成 - 总字节:', decompressedUint8.byteLength,
                        ' | 作为Float32:', Math.floor(decompressedUint8.byteLength / 4),
                        ' | 对应点数:', Math.floor(decompressedUint8.byteLength / 4));

            // 解压后数据直接就是 float[] 数组，无需再跳过
            // （外层文件头的8字节已在前面跳过，提取了LZ4压缩数据）
            return new Uint8Array(decompressedUint8).buffer;

        } catch (e) {
            console.error('LZ4 解压过程中出错:', e);
            return null;
        }
    }

    /**
     * 解析二进制数据
     * @param {ArrayBuffer} buffer 数据缓冲区
     * @param {Object} header 头部信息
     * @returns {Array<Object>} 点数据数组
     */
    static parseBinaryData(buffer, header) {
        const points = [];
        const dataView = new DataView(buffer);
        const numPoints = header.pointsCount;

        // 查找字段索引
        const getFieldIndex = (name) => header.fields.findIndex(f => f.toLowerCase() === name.toLowerCase());

        const xIndex = getFieldIndex('x');
        const yIndex = getFieldIndex('y');
        const zIndex = getFieldIndex('z');
        const rgbIndex = getFieldIndex('rgb');
        const rgbaIndex = getFieldIndex('rgba');
        const labelIndex = getFieldIndex('label');

        let byteOffset = 0;

        for (let i = 0; i < numPoints; i++) {
            const point = {};
            let rgbaByteOffset = null; // 保存  字段的起始偏移rgba

            for (let j = 0; j < header.fields.length; j++) {
                const fieldName = header.fields[j].toLowerCase();
                const type = header.types[j];
                const size = header.sizes[j];
                const count = header.counts[j] || 1;

                // 如果是 rgba 字段，保存偏移
                if (fieldName === 'rgba') {
                    rgbaByteOffset = byteOffset;
                }

                // 读取数据
                if (type === 'F') {  // Float
                    if (size === 4) {
                        point[fieldName] = dataView.getFloat32(byteOffset, true);
                    } else if (size === 8) {
                        point[fieldName] = dataView.getFloat64(byteOffset, true);
                    }
                } else if (type === 'I') {  // Signed Integer
                    if (size === 1) {
                        point[fieldName] = dataView.getInt8(byteOffset);
                    } else if (size === 2) {
                        point[fieldName] = dataView.getInt16(byteOffset, true);
                    } else if (size === 4) {
                        point[fieldName] = dataView.getInt32(byteOffset, true);
                    }
                } else if (type === 'U') {  // Unsigned Integer
                    if (size === 1) {
                        point[fieldName] = dataView.getUint8(byteOffset);
                    } else if (size === 2) {
                        point[fieldName] = dataView.getUint16(byteOffset, true);
                    } else if (size === 4) {
                        point[fieldName] = dataView.getUint32(byteOffset, true);
                    }
                }

                byteOffset += size * count;
            }

            // 处理 rgba 格式（转换为 r, g, b, a）
            // PCL 中 rgba 存储格式为 0xAABBGGRR (从高位到低位：A, B, G, R)
            if (rgbaIndex >= 0 && point.rgba !== undefined && rgbaByteOffset !== null) {
                const rgba = point.rgba;

                // 调试：前几个点的原始 rgba 值
                if (points.length < 5) {
                    console.log(`点 ${points.length} 原始 rgba (十六进制): 0x${rgba.toString(16).padStart(8, '0')}`);
                    console.log(`点 ${points.length} 原始 rgba (十进制): ${rgba}`);
                }

                // 尝试大端序读取 rgba（如果是 4 字节）
                if (header.sizes[rgbaIndex] === 4) {
                    // 使用大端序读取 rgba
                    if (header.types[rgbaIndex] === 'U') {

                        const r = dataView.getUint8(rgbaByteOffset + 0);
                        const g = dataView.getUint8(rgbaByteOffset + 1);
                        const b = dataView.getUint8(rgbaByteOffset + 2);
                        const a = dataView.getUint8(rgbaByteOffset + 3);

                        point.r = r;
                        point.g = g;
                        point.b = b;
                        point.a = a;
                    }
                }
            }

            points.push(point);
        }

        return points;
    }

    /**
     * 计算边界框
     * @param {Array<Object>} points 点数据数组
     * @returns {THREE.Box3} 边界框
     */
    static computeBoundingBox(points) {
        const min = new THREE.Vector3(Infinity, Infinity, Infinity);
        const max = new THREE.Vector3(-Infinity, -Infinity, -Infinity);

        for (const point of points) {
            if (point.x < min.x) min.x = point.x;
            if (point.y < min.y) min.y = point.y;
            if (point.z < min.z) min.z = point.z;

            if (point.x > max.x) max.x = point.x;
            if (point.y > max.y) max.y = point.y;
            if (point.z > max.z) max.z = point.z;
        }

        return new THREE.Box3(min, max);
    }

    /**
     * 计算点间距
     * @param {THREE.Box3} bbox 边界框
     * @param {number} numPoints 点数量
     * @returns {number} 间距
     */
    static computeSpacing(bbox, numPoints) {
        const volume = (bbox.max.x - bbox.min.x) *
                     (bbox.max.y - bbox.min.y) *
                     (bbox.max.z - bbox.min.z);
        return Math.pow(volume / numPoints, 1/3);
    }

    /**
     * 创建 BufferGeometry
     * @param {Array<Object>} points 点数据数组
     * @param {Object} header 头部信息
     * @param {THREE.Vector3} offset 偏移量
     * @returns {THREE.BufferGeometry} BufferGeometry
     */
    static createBufferGeometry(points, header, offset) {



        const geometry = new THREE.BufferGeometry();
        const numPoints = points.length;

        // 位置属性
        const positions = new Float32Array(numPoints * 3);
        const colors = new Uint8Array(numPoints * 4); // 改为4字节 RGBA
        const temperature = new Float32Array(numPoints); // 用于temperature（映射为temperature属性）
        const labels = new Float32Array(numPoints);
        const selected = new Uint8Array(numPoints);
        // indices属性 - 存储点索引（仿照LasLazLoader）
        // 使用Uint32Array存储索引，然后作为Uint8Array读取（4个字节/点）
        const indicesBuffer = new ArrayBuffer(numPoints * 4);
        const indicesUint32 = new Uint32Array(indicesBuffer);
        const indices = new Uint8Array(indicesBuffer);

        // 查找字段索引
        const getFieldIndex = (name) => header.fields.findIndex(f => f.toLowerCase() === name.toLowerCase());
        const rgbaIndex = getFieldIndex('rgba');
        const labelIndex = getFieldIndex('label');

        for (let i = 0; i < numPoints; i++) {
            const p = points[i];
            const i3 = i * 3;

            // 位置（减去偏移量）
            positions[i3] = p.x - offset.x;
            positions[i3 + 1] = p.y - offset.y;
            positions[i3 + 2] = p.z - offset.z;

            // 颜色 (RGBA格式：R, G, B, A)
            if (rgbaIndex >= 0 && p.rgba !== undefined) {
                const i4 = i * 4;
                colors[i4] = p.b ;
                colors[i4 + 1] = p.g ;
                colors[i4 + 2] = p.r ;
                colors[i4 + 3] = p.a ;
            } else {
                // 默认白色
                const i4 = i * 4;
                colors[i4] = 0;
                colors[i4 + 1] = 0;
                colors[i4 + 2] = 0;
                colors[i4 + 3] = 255;
            }

            // 标签
            labels[i] = p.label || 0;

            temperature[i] = p.temperatureValue;

            // 选择状态（默认为0）
            selected[i] = 0;

            // indices - 存储点索引（用于拾取功能）
            // 在Uint32Array中存储点的索引，作为Uint8Array读取时自动分成4个字节
            indicesUint32[i] = i;
        }

        geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        geometry.setAttribute('rgba', new THREE.BufferAttribute(colors, 4, true));
        geometry.setAttribute('temperature', new THREE.BufferAttribute(temperature, 1));
        geometry.setAttribute('classification', new THREE.BufferAttribute(labels, 1));
        geometry.setAttribute('indices', new THREE.BufferAttribute(indices, 4));
        geometry.attributes.indices.normalized = true;
        geometry.setAttribute('isSelected', new THREE.BufferAttribute(new Uint8Array(numPoints), 1));

        geometry.computeBoundingSphere();
        geometry.computeBoundingBox();

        return geometry;
    }
}

/**
 * 创建 PCD 点属性
 * @param {Object} header PCD 头部信息
 * @returns {PointAttributes} 点属性集合
 */
function pcdAttributes(header) {
    const attributes = new PointAttributes();

    attributes.add(PointAttribute.POSITION_CARTESIAN);

    // 检查是否有颜色字段
    const hasRgba = header.fields.some(f => f.toLowerCase() === 'rgba');
    const hasRgb = header.fields.some(f => f.toLowerCase() === 'rgb');

    if (hasRgba || hasRgb) {
        attributes.add(new PointAttribute("rgba", PointAttributeTypes.DATA_TYPE_UINT8, 4));
    } else {
        // 默认白色
        attributes.add(new PointAttribute("rgba", PointAttributeTypes.DATA_TYPE_UINT8, 4));
    }

    // 检查是否有标签字段
    const hasLabel = header.fields.some(f => f.toLowerCase() === 'label');
    if (hasLabel) {
        attributes.add(new PointAttribute("classification", PointAttributeTypes.DATA_TYPE_FLOAT, 1));
    }

    // 添加temperature属性（用于temperature等额外数据）
    attributes.add(new PointAttribute("temperature", PointAttributeTypes.DATA_TYPE_FLOAT, 1));

    // 添加indices属性（仿照LasLazLoader）
    attributes.add(new PointAttribute("indices", PointAttributeTypes.DATA_TYPE_UINT8, 4));

    attributes.add(new PointAttribute("isSelected", PointAttributeTypes.DATA_TYPE_UINT8, 1));

    return attributes;
}
